/**
 * Phira's note walk with "aggressive" culling (W1-06; R§4.5 item 7, F§11.4, A§7.2).
 *
 * Phira's literal loop (`walkLineNotesLiteral`, the oracle): per line and side (above, then below), the non-plain
 * notes of that side (holds and fakes) are always visited; then every plain group of that side (same side,
 * bit-identical f32 speed, sorted by (height + yOff) * speed) is walked FROM ITS HEAD: judged notes are skipped,
 * and with aggressive culling the walk BREAKS at the first note whose raw distance `(height - lineHeight) + yOff`
 * exceeds `heightLimit / speed`. Nothing searches by time and there is no lower cull, exactly like Phira, so the
 * quirks follow by construction:
 *   - the limit ignores yControl (notes pulled on screen by yControl < 1 can still be culled);
 *   - with a negative speed the limit is negative and the group (farthest-ahead first) breaks immediately while
 *     its first unjudged note is ahead of the line, so the whole group is skipped;
 *   - speeds are grouped by exact equality (1.0 and 1.0000001 are different groups).
 * The frame walk (`walkLineNotes`, W3-02a for UX-025) visits and culls the same notes in the same order, but starts
 * each plain group after its leading run of judged notes (autoplay) and each side's non-plain notes after their
 * leading run of finished holds (visitors with `ignoresGone`), remembered across frames instead of re-tested from
 * the head; culling.test.ts checks it against the literal loop.
 * `heightLimit` is the max (above) / -min (below) line-local y of the world points (+-1.1, +-1), times ar.
 */

import type { JudgeView } from '../types';
import type { Mat2D } from '../../util/types';
import { CULL_POINT_X, CULL_POINT_Y } from './constants';
import { NOTE_HOLD, type LineNotesRt } from './runtime';
import { isJudged } from './visibility';

const fr = Math.fround;

export interface CullLimits {
  /** heightLimit of the above side (line-local +y). */
  above: number;
  /** heightLimit of the below side. */
  below: number;
}

/** heightLimit for both sides from the line matrix (local -> world) and the aspect ratio. */
export function cullLimits(m: Mat2D, ar: number, out: CullLimits = { above: 0, below: 0 }): CullLimits {
  const [a, b, c, d, e, f] = m;
  const det = a * d - b * c;
  let maxY = -Infinity;
  let minY = Infinity;
  for (let sx = -1; sx <= 1; sx += 2) {
    for (let sy = -1; sy <= 1; sy += 2) {
      const dx = sx * CULL_POINT_X - e;
      const dy = sy * CULL_POINT_Y - f;
      const ly = (a * dy - b * dx) / det;
      if (ly > maxY) maxY = ly;
      if (ly < minY) minY = ly;
    }
  }
  out.above = fr(maxY * ar);
  out.below = fr(-minY * ar);
  return out;
}

/**
 * Line-local extent of the TRUE visible area [-1, 1] x [-1/ar, 1/ar]: the max local y (above side) and -min local
 * y (below side) of its corners, in world units (ghost scanning bound; not a Phira rule).
 */
export function viewLimits(m: Mat2D, ar: number, out: CullLimits = { above: 0, below: 0 }): CullLimits {
  const [a, b, c, d, e, f] = m;
  const det = a * d - b * c;
  let maxY = -Infinity;
  let minY = Infinity;
  for (let sx = -1; sx <= 1; sx += 2) {
    for (let sy = -1; sy <= 1; sy += 2) {
      const dx = sx - e;
      const dy = sy / ar - f;
      const ly = (a * dy - b * dx) / det;
      if (ly > maxY) maxY = ly;
      if (ly < minY) minY = ly;
    }
  }
  out.above = maxY;
  out.below = -minY;
  return out;
}

/** Raw distance test of the break: `(height - lineHeight) + yOff > limit / speed` in f32. */
export function exceedsLimit(rt: LineNotesRt, i: number, lineHeight: number, limit: number): boolean {
  const dist = fr(fr((rt.height[i] ?? 0) - lineHeight) + (rt.yOff[i] ?? 0));
  return dist > fr(limit / (rt.speed[i] ?? 0));
}

/** Receives the notes of a line in Phira's push order. */
export interface NoteWalkVisitor {
  /** A note the walk reaches: every non-plain note, and plain notes that are neither judged nor culled. */
  visit(rt: LineNotesRt, i: number): void;
  /** Called for every unjudged plain note cut off by an aggressive break (from the breaking note on). */
  culled?(rt: LineNotesRt, i: number): void;
  /**
   * The visitor ignores non-plain notes that are gone (`isGone`: holds past their end); the walk may then skip a
   * leading run of them (`walkLineNotes`). Leave unset when every visit matters.
   */
  readonly ignoresGone?: boolean;
}

/**
 * Phira's literal walk of one line in push order (non-plain above, plain above, non-plain below, plain below): the
 * reference form of `walkLineNotes`, kept as its oracle (A§7.2). `judge` null = autoplay.
 */
export function walkLineNotesLiteral(
  rt: LineNotesRt,
  lineHeight: number,
  limits: CullLimits,
  aggressive: boolean,
  t: number,
  judge: JudgeView | null,
  visitor: NoteWalkVisitor,
): void {
  for (let side = 0; side < 2; side++) {
    const above = side === 0;
    const npStart = above ? 0 : rt.nonPlainAboveEnd;
    const npEnd = above ? rt.nonPlainAboveEnd : rt.nonPlainEnd;
    for (let i = npStart; i < npEnd; i++) visitor.visit(rt, i);
    const gStart = above ? 0 : rt.aboveGroupCount;
    const gEnd = above ? rt.aboveGroupCount : rt.groupCount;
    const limit = above ? limits.above : limits.below;
    for (let g = gStart; g < gEnd; g++) {
      const end = rt.groupEnd[g] ?? 0;
      for (let i = rt.groupStart[g] ?? 0; i < end; i++) {
        if (isJudged(rt, i, t, judge)) continue;
        if (aggressive && exceedsLimit(rt, i, lineHeight, limit)) {
          if (visitor.culled !== undefined) {
            for (let j = i; j < end; j++) if (!isJudged(rt, j, t, judge)) visitor.culled(rt, j);
          }
          break;
        }
        visitor.visit(rt, i);
      }
    }
  }
}

/**
 * Leading runs a walk may skip at time `t` (W3-02a, UX-025): per plain group, the first index whose prefix is
 * entirely judged in autoplay (`t >= time` for every note before it), and per side the first non-plain index
 * whose prefix is entirely gone holds (`t >= endTime`). Both properties are monotone in t, so a forward-moving
 * time advances the stored indices and a backward seek starts again from the heads. Holds are gone and plain notes
 * judged at t exactly as the literal walk tests them, so skipping the runs leaves the visited and culled notes and
 * their order unchanged.
 */
interface WalkPrefix {
  t: number;
  readonly group: Int32Array;
  readonly nonPlain: Int32Array;
}

const walkPrefixes = new WeakMap<LineNotesRt, WalkPrefix>();

function walkPrefixOf(rt: LineNotesRt, t: number): WalkPrefix {
  let p = walkPrefixes.get(rt);
  if (p === undefined) {
    p = { t: Number.NaN, group: new Int32Array(rt.groupCount), nonPlain: new Int32Array(2) };
    walkPrefixes.set(rt, p);
  }
  if (!(t >= p.t)) {
    // First use, a backward seek or a NaN time: start from the heads.
    for (let g = 0; g < rt.groupCount; g++) p.group[g] = rt.groupStart[g] ?? 0;
    p.nonPlain[0] = 0;
    p.nonPlain[1] = rt.nonPlainAboveEnd;
  }
  p.t = t;
  return p;
}

/**
 * Walks one line in Phira's push order (non-plain above, plain above, non-plain below, plain below), visiting the
 * same notes in the same order as Phira's literal loop (`walkLineNotesLiteral`, its oracle in culling.test.ts) but
 * without re-testing the leading runs of judged plain notes (autoplay) and of gone holds (visitors with
 * `ignoresGone`) on every frame. `judge` null = autoplay.
 */
export function walkLineNotes(
  rt: LineNotesRt,
  lineHeight: number,
  limits: CullLimits,
  aggressive: boolean,
  t: number,
  judge: JudgeView | null,
  visitor: NoteWalkVisitor,
): void {
  const skipGone = visitor.ignoresGone === true;
  const skipJudged = judge === null;
  if ((!skipGone && !skipJudged) || Number.isNaN(t)) {
    walkLineNotesLiteral(rt, lineHeight, limits, aggressive, t, judge, visitor);
    return;
  }
  const prefix = walkPrefixOf(rt, t);
  for (let side = 0; side < 2; side++) {
    const above = side === 0;
    const npEnd = above ? rt.nonPlainAboveEnd : rt.nonPlainEnd;
    let npStart = above ? 0 : rt.nonPlainAboveEnd;
    if (skipGone) {
      let i = prefix.nonPlain[side] ?? npStart;
      while (i < npEnd && rt.type[i] === NOTE_HOLD && t >= (rt.endTime[i] ?? Number.NaN)) i++;
      prefix.nonPlain[side] = i;
      npStart = i;
    }
    for (let i = npStart; i < npEnd; i++) visitor.visit(rt, i);
    const gStart = above ? 0 : rt.aboveGroupCount;
    const gEnd = above ? rt.aboveGroupCount : rt.groupCount;
    const limit = above ? limits.above : limits.below;
    for (let g = gStart; g < gEnd; g++) {
      const end = rt.groupEnd[g] ?? 0;
      let i = rt.groupStart[g] ?? 0;
      if (skipJudged) {
        // Plain notes are real: judged in autoplay exactly when t >= time.
        let k = prefix.group[g] ?? i;
        while (k < end && t >= (rt.time[k] ?? Number.NaN)) k++;
        prefix.group[g] = k;
        i = k;
      }
      for (; i < end; i++) {
        if (isJudged(rt, i, t, judge)) continue;
        if (aggressive && exceedsLimit(rt, i, lineHeight, limit)) {
          if (visitor.culled !== undefined) {
            for (let j = i; j < end; j++) if (!isJudged(rt, j, t, judge)) visitor.culled(rt, j);
          }
          break;
        }
        visitor.visit(rt, i);
      }
    }
  }
}

/**
 * Per-runtime index of the single-note cull query (built once per runtime, which is immutable).
 *
 * `prefMax[j]` = max over the group prefix [groupStart, j] of the exact key `height + yOff` (f64); `mag[g]` = max
 * over group g of `|height| + |yOff|`. The f32 raw distance of note j, `fr(fr(height - L) + yOff)`, differs from the
 * exact `height + yOff - L` by at most ~2^-23 (|height| + |L| + |yOff|) (two roundings to f32), so
 * `prefMax[j] - L + CULL_ERR_REL * (mag + |L|) + CULL_ERR_ABS <= c` proves that NO note in [groupStart, j] exceeds
 * the break threshold c, whatever the f32 rounding does to the order. The margins are far wider than the bound.
 */
interface CullIndex {
  /** Group of each note (-1 for non-plain notes). */
  readonly groupOf: Int32Array;
  readonly prefMax: Float64Array;
  readonly mag: Float64Array;
}

/** Relative and absolute margins of the f32 rounding bound above (2^-20 is 8x the worst case of 2^-23). */
const CULL_ERR_REL = 2 ** -20;
const CULL_ERR_ABS = 1e-30;

const cullIndexes = new WeakMap<LineNotesRt, CullIndex>();

function cullIndexOf(rt: LineNotesRt): CullIndex {
  let ix = cullIndexes.get(rt);
  if (ix !== undefined) return ix;
  const groupOf = new Int32Array(rt.n).fill(-1);
  const prefMax = new Float64Array(rt.n);
  const mag = new Float64Array(rt.groupCount);
  for (let g = 0; g < rt.groupCount; g++) {
    const end = rt.groupEnd[g] ?? 0;
    let max = -Infinity;
    let m = 0;
    for (let j = rt.groupStart[g] ?? 0; j < end; j++) {
      const h = rt.height[j] ?? 0;
      const y = rt.yOff[j] ?? 0;
      const k = h + y;
      // NaN propagates (Math.max), which disables the early stop: the scan then visits every note, still exact.
      max = Math.max(max, k);
      m = Math.max(m, Math.abs(h) + Math.abs(y));
      groupOf[j] = g;
      prefMax[j] = max;
    }
    mag[g] = m;
  }
  ix = { groupOf, prefMax, mag };
  cullIndexes.set(rt, ix);
  return ix;
}

/**
 * Whether the aggressive walk would cut note `i` (a plain note) at t: some unjudged note of its group at or before
 * it in sort order exceeds the limit. Used by single-note queries (noteAppearSec, once per probed frame); equals the
 * walk by definition.
 *
 * The group prefix is scanned BACKWARDS from i and the scan stops as soon as the prefix maximum of the exact key
 * proves that no remaining note can exceed the threshold (see CullIndex). For positive speeds the sort order is the
 * order of raw distance, so the answer is found at i itself: O(1) instead of a walk from the group head. Negative
 * speeds (farthest-ahead first) are decided by the first unjudged note ahead of the line, normally i or a close
 * neighbour. Every note the scan does reach is tested exactly as the walk tests it.
 *
 * Scope: read-time queries only (the frame walk is `walkLineNotes`, whose prefix skipping W3-02a added for
 * UX-025; Phira's literal loop is `walkLineNotesLiteral`, its oracle). culling.test.ts checks this early stop
 * against the literal prefix walk from the group head (`culledInWalkLiteral`), which stays the oracle.
 */
export function culledInWalk(
  rt: LineNotesRt,
  i: number,
  lineHeight: number,
  limits: CullLimits,
  t: number,
  judge: JudgeView | null,
): boolean {
  if (rt.plain[i] !== 1) return false;
  const ix = cullIndexOf(rt);
  const g = ix.groupOf[i] ?? -1;
  if (g < 0) return false;
  const limit = g < rt.aboveGroupCount ? limits.above : limits.below;
  // The walk's threshold (one speed per group): nothing exceeds NaN or +Infinity.
  const c = fr(limit / (rt.speed[i] ?? 0));
  if (Number.isNaN(c) || c === Infinity) return false;
  const err = CULL_ERR_REL * ((ix.mag[g] ?? 0) + Math.abs(lineHeight)) + CULL_ERR_ABS;
  const start = rt.groupStart[g] ?? 0;
  for (let j = i; j >= start; j--) {
    if ((ix.prefMax[j] ?? Number.NaN) - lineHeight + err < c) return false;
    if (isJudged(rt, j, t, judge)) continue;
    if (exceedsLimit(rt, j, lineHeight, limit)) return true;
  }
  return false;
}

/** Reference form of culledInWalk (the literal prefix walk from the group head); tests compare the two. */
export function culledInWalkLiteral(
  rt: LineNotesRt,
  i: number,
  lineHeight: number,
  limits: CullLimits,
  t: number,
  judge: JudgeView | null,
): boolean {
  if (rt.plain[i] !== 1) return false;
  let g = 0;
  while (g < rt.groupCount && (rt.groupEnd[g] ?? 0) <= i) g++;
  const limit = g < rt.aboveGroupCount ? limits.above : limits.below;
  for (let j = rt.groupStart[g] ?? 0; j <= i; j++) {
    if (isJudged(rt, j, t, judge)) continue;
    if (exceedsLimit(rt, j, lineHeight, limit)) return true;
  }
  return false;
}
